import React from 'react';
import { KeyRound, Loader2, MessageSquare, RefreshCw, Shield, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { bsiInternetBankApi, type BsiIbProfileSummary, type BsiIbStatus } from '@/lib/banks/bsi/api';
import type { BsiFlashMessage } from './helpers';

/**
 * مطابق فرم رسمی bib.bsi.ir/security/login-process-form.ib:
 * - 52 پیامک: کپچا همیشه لازم + ارسال پیامک سپس شناسه تأیید
 * - 53 توکن سخت‌افزاری: فقط نام کاربری + کلمه عبور، بدون کپچا (مگر بعد از ۲ خطای متوالی)
 * - 54 موبایل/USSD: مثل توکن، بدون کپچا
 */
const AUTH_OPTIONS = [
  { value: '52', label: 'پیامک', hint: 'رمز ایستا + کد پیامک' },
  { value: '53', label: 'توکن', hint: 'رمزیاب سخت‌افزاری' },
  { value: '54', label: 'موبایل', hint: 'نرم‌افزار / USSD' },
] as const;

type AuthGroup = (typeof AUTH_OPTIONS)[number]['value'];

type Props = {
  status: BsiIbStatus | null;
  onStatusChange: (status: BsiIbStatus) => void;
  onMessage: (message: BsiFlashMessage | null) => void;
  onLoggedIn: () => void;
};

function authNeedsCaptchaByDefault(group: string): boolean {
  return group === '52' || group === '51';
}

function profileHasCredentials(p: BsiIbProfileSummary): boolean {
  return Boolean(p.default_username?.trim()) && Boolean(p.has_password);
}

function profileOptionLabel(p: BsiIbProfileSummary): string {
  const parts = [p.name];
  if (p.owner_name) parts.push(p.owner_name);
  if (p.default_username) parts.push(p.default_username);
  return parts.join(' — ');
}

const BsiLoginTab: React.FC<Props> = ({ status, onStatusChange, onMessage, onLoggedIn }) => {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [captcha, setCaptcha] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [authGroup, setAuthGroup] = React.useState<AuthGroup>('52');
  const [captchaSrc, setCaptchaSrc] = React.useState('');
  const [loadingCaptcha, setLoadingCaptcha] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [sendingSms, setSendingSms] = React.useState(false);
  const [needsOtp, setNeedsOtp] = React.useState(false);
  const [forceCaptcha, setForceCaptcha] = React.useState(false);
  const [ocrAvailable, setOcrAvailable] = React.useState<boolean | null>(null);
  const [captchaAutoFilled, setCaptchaAutoFilled] = React.useState(false);
  const [selectedProfileId, setSelectedProfileId] = React.useState<number | null>(null);
  const [switchingProfile, setSwitchingProfile] = React.useState(false);
  const sessionReadyRef = React.useRef(false);
  const autoActivatedRef = React.useRef<number | null>(null);

  const credentialProfiles = React.useMemo(
    () => (status?.profiles ?? []).filter(profileHasCredentials),
    [status?.profiles],
  );

  const selectedProfile =
    credentialProfiles.find((p) => p.id === selectedProfileId) ??
    credentialProfiles.find((p) => p.is_active_selection) ??
    (credentialProfiles.length === 1 ? credentialProfiles[0] : null);

  const isSmsFlow = authGroup === '52';
  const showCaptcha = authNeedsCaptchaByDefault(authGroup) || forceCaptcha;
  const step: 1 | 2 = isSmsFlow && needsOtp ? 2 : 1;
  const hasStoredUser = Boolean(selectedProfile?.default_username?.trim() || status?.default_username?.trim());
  const hasStoredPass = Boolean(selectedProfile?.has_password || status?.has_default_password);
  const useStoredCredentials = Boolean(selectedProfile) && hasStoredUser && hasStoredPass && authGroup !== '54';
  const needsAccountPick = credentialProfiles.length > 1 && !selectedProfile;

  const applyCaptchaImage = React.useCallback(
    (base64: string, mime?: string | null, suggestion?: string | null, ocrOk?: boolean | null) => {
      const safeMime = mime || 'image/jpeg';
      setCaptchaSrc(`data:${safeMime};base64,${base64}`);
      const auto = (suggestion || '').trim();
      if (auto) {
        setCaptcha(auto);
        setCaptchaAutoFilled(true);
      } else {
        setCaptcha('');
        setCaptchaAutoFilled(false);
      }
      if (typeof ocrOk === 'boolean') setOcrAvailable(ocrOk);
      try {
        sessionStorage.setItem(
          'bsi_ib_captcha_cache',
          JSON.stringify({
            base64,
            mime: safeMime,
            suggestion: auto || null,
            ocrAvailable: ocrOk ?? null,
            at: Date.now(),
          }),
        );
      } catch {
        // ignore quota
      }
    },
    [],
  );

  const clearCaptchaCache = React.useCallback(() => {
    try {
      sessionStorage.removeItem('bsi_ib_captcha_cache');
    } catch {
      // ignore
    }
    setCaptchaSrc('');
    setCaptcha('');
    setCaptchaAutoFilled(false);
    sessionReadyRef.current = false;
  }, []);

  const ensureLoginSession = React.useCallback(
    async (opts?: { force?: boolean; withImage?: boolean }) => {
      const force = opts?.force ?? false;
      const withImage = opts?.withImage ?? true;
      setLoadingCaptcha(true);
      try {
        const res = await bsiInternetBankApi.captcha(force);
        sessionReadyRef.current = true;
        if (withImage && res.data.captcha_base64) {
          applyCaptchaImage(
            res.data.captcha_base64,
            res.data.captcha_mime,
            res.data.captcha_suggestion,
            res.data.ocr_available,
          );
        }
        return true;
      } catch (e) {
        onMessage({ type: 'error', text: e instanceof Error ? e.message : 'خطا در آماده‌سازی ورود' });
        return false;
      } finally {
        setLoadingCaptcha(false);
      }
    },
    [applyCaptchaImage, onMessage],
  );

  const activateLoginProfile = React.useCallback(
    async (profileId: number, opts?: { silent?: boolean }) => {
      if (status?.profile?.id === profileId) {
        setSelectedProfileId(profileId);
        return true;
      }
      setSwitchingProfile(true);
      try {
        const res = await bsiInternetBankApi.activateProfile(profileId);
        onStatusChange(res.data.status);
        setSelectedProfileId(profileId);
        clearCaptchaCache();
        setNeedsOtp(false);
        setOtp('');
        setForceCaptcha(false);
        if (!opts?.silent) {
          onMessage({ type: 'success', text: res.message || 'حساب برای ورود انتخاب شد.' });
        }
        await ensureLoginSession({ force: true, withImage: authNeedsCaptchaByDefault(authGroup) });
        return true;
      } catch (e) {
        onMessage({ type: 'error', text: e instanceof Error ? e.message : 'انتخاب حساب ناموفق بود.' });
        return false;
      } finally {
        setSwitchingProfile(false);
      }
    },
    [authGroup, clearCaptchaCache, ensureLoginSession, onMessage, onStatusChange, status?.profile?.id],
  );

  // یک حساب با اعتبار → خودکار؛ چند حساب → اگر فعال فعلی معتبر است همان، وگرنه کاربر انتخاب کند
  React.useEffect(() => {
    if (credentialProfiles.length === 0) {
      setSelectedProfileId(null);
      return;
    }
    if (credentialProfiles.length === 1) {
      const only = credentialProfiles[0];
      setSelectedProfileId(only.id);
      if (status?.profile?.id !== only.id && autoActivatedRef.current !== only.id) {
        autoActivatedRef.current = only.id;
        void activateLoginProfile(only.id, { silent: true });
      }
      return;
    }
    const active = credentialProfiles.find((p) => p.is_active_selection || p.id === status?.profile?.id);
    if (active) {
      setSelectedProfileId((prev) => prev ?? active.id);
    }
  }, [activateLoginProfile, credentialProfiles, status?.profile?.id]);

  React.useEffect(() => {
    const nextUser = selectedProfile?.default_username || status?.default_username || '';
    if (nextUser) {
      setUsername(nextUser);
    }
    setNeedsOtp(Boolean(status?.needs_otp));
  }, [selectedProfile?.default_username, status?.default_username, status?.needs_otp, selectedProfile?.id]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (showCaptcha) {
        try {
          const raw = sessionStorage.getItem('bsi_ib_captcha_cache');
          if (raw) {
            const cached = JSON.parse(raw) as {
              base64?: string;
              mime?: string;
              suggestion?: string | null;
              ocrAvailable?: boolean | null;
              at?: number;
            };
            const fresh = typeof cached.at === 'number' && Date.now() - cached.at < 10 * 60 * 1000;
            if (fresh && cached.base64) {
              if (!cancelled) {
                applyCaptchaImage(cached.base64, cached.mime, cached.suggestion, cached.ocrAvailable);
                sessionReadyRef.current = true;
              }
              return;
            }
          }
        } catch {
          // fall through
        }
      }
      if (!cancelled) {
        await ensureLoginSession({ force: true, withImage: showCaptcha });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyCaptchaImage, ensureLoginSession, showCaptcha]);

  const resetFlow = () => {
    setNeedsOtp(false);
    setOtp('');
    setForceCaptcha(false);
    setCaptchaAutoFilled(false);
    void ensureLoginSession({ force: true, withImage: authNeedsCaptchaByDefault(authGroup) });
  };

  const handleAuthChange = (next: AuthGroup) => {
    if (needsOtp) return;
    setAuthGroup(next);
    setOtp('');
    setForceCaptcha(false);
    setNeedsOtp(false);
    void ensureLoginSession({ force: false, withImage: authNeedsCaptchaByDefault(next) });
  };

  const handleAccountChange = (profileId: number) => {
    if (needsOtp || switchingProfile) return;
    setSelectedProfileId(profileId);
    void activateLoginProfile(profileId);
  };

  const maybeForceCaptchaFromError = (message: string) => {
    const lower = message.toLowerCase();
    if (
      message.includes('کپچا') ||
      message.includes('عبارت امنیتی') ||
      lower.includes('captcha') ||
      lower.includes('showcaptcha')
    ) {
      setForceCaptcha(true);
      void ensureLoginSession({ force: false, withImage: true });
    }
  };

  const ensureSelectedBeforeAuth = async (): Promise<boolean> => {
    if (credentialProfiles.length === 0) return true;
    if (!selectedProfile) {
      onMessage({ type: 'error', text: 'ابتدا حساب / نام کاربری را انتخاب کنید.' });
      return false;
    }
    if (status?.profile?.id !== selectedProfile.id) {
      return activateLoginProfile(selectedProfile.id, { silent: true });
    }
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(await ensureSelectedBeforeAuth())) return;
    if (!useStoredCredentials && !username.trim()) {
      onMessage({ type: 'error', text: 'نام کاربری را وارد کنید یا در پروفایل ذخیره کنید.' });
      return;
    }
    if (authGroup === '54' && !password.trim()) {
      onMessage({ type: 'error', text: 'کد موبایل / USSD را وارد کنید.' });
      return;
    }
    if (!useStoredCredentials && authGroup !== '54' && !password.trim() && !hasStoredPass) {
      onMessage({ type: 'error', text: 'رمز عبور را وارد کنید یا در پروفایل ذخیره کنید.' });
      return;
    }
    if (showCaptcha && !captcha.trim()) {
      onMessage({ type: 'error', text: 'کد کپچا را وارد کنید.' });
      return;
    }
    if (isSmsFlow && !needsOtp) {
      onMessage({ type: 'error', text: 'ابتدا پیامک را ارسال کنید.' });
      return;
    }
    if (isSmsFlow && !otp.trim()) {
      onMessage({ type: 'error', text: 'کد پیامک را وارد کنید.' });
      return;
    }
    if (!sessionReadyRef.current) {
      const ok = await ensureLoginSession({ force: true, withImage: showCaptcha });
      if (!ok) return;
    }

    setSubmitting(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.login({
        username: useStoredCredentials ? undefined : username.trim() || undefined,
        password:
          useStoredCredentials || (hasStoredPass && !password.trim() && authGroup !== '54')
            ? undefined
            : password || undefined,
        captcha: showCaptcha ? captcha : '',
        authentication_group: authGroup,
        otp: isSmsFlow ? otp || undefined : undefined,
        use_default_password:
          useStoredCredentials || (hasStoredPass && !password.trim() && authGroup !== '54'),
      });
      onStatusChange(res.data.status);
      setNeedsOtp(Boolean(res.data.needs_otp));
      if (res.data.show_captcha) {
        setForceCaptcha(true);
        await ensureLoginSession({ force: false, withImage: true });
      }
      if (res.data.logged_in) {
        onMessage({ type: 'success', text: res.message || 'ورود موفق بود.' });
        setPassword('');
        setCaptcha('');
        setCaptchaAutoFilled(false);
        setOtp('');
        setForceCaptcha(false);
        clearCaptchaCache();
        onLoggedIn();
      } else if (res.data.needs_otp) {
        onMessage({ type: 'success', text: res.message || 'کد یک‌بارمصرف را وارد کنید.' });
        if (showCaptcha) await ensureLoginSession({ force: false, withImage: true });
      } else {
        onMessage({ type: 'success', text: res.message || 'درخواست ارسال شد.' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'ورود ناموفق بود.';
      onMessage({ type: 'error', text: msg });
      maybeForceCaptchaFromError(msg);
      if (showCaptcha || forceCaptcha) {
        await ensureLoginSession({ force: false, withImage: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendSms = async () => {
    if (!(await ensureSelectedBeforeAuth())) return;
    if (!useStoredCredentials && !username.trim()) {
      onMessage({ type: 'error', text: 'نام کاربری را وارد کنید یا در پروفایل ذخیره کنید.' });
      return;
    }
    if (!useStoredCredentials && !password.trim() && !hasStoredPass) {
      onMessage({ type: 'error', text: 'رمز عبور را وارد کنید یا در پروفایل ذخیره کنید.' });
      return;
    }
    if (!captcha.trim()) {
      onMessage({ type: 'error', text: 'کد کپچا را وارد کنید.' });
      return;
    }
    setSendingSms(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.sendSms({
        username: useStoredCredentials ? undefined : username.trim() || undefined,
        password: useStoredCredentials || (hasStoredPass && !password.trim()) ? undefined : password || undefined,
        captcha,
        authentication_group: '52',
      });
      setNeedsOtp(true);
      setAuthGroup('52');
      onStatusChange(res.data.status);
      if (res.data.captcha_base64) {
        applyCaptchaImage(
          res.data.captcha_base64,
          res.data.captcha_mime,
          res.data.captcha_suggestion,
          res.data.ocr_available,
        );
      } else {
        await ensureLoginSession({ force: false, withImage: true });
      }
      onMessage({ type: 'success', text: res.message || 'پیامک ارسال شد.' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'ارسال پیامک ناموفق بود.';
      onMessage({ type: 'error', text: msg });
      await ensureLoginSession({ force: false, withImage: true });
    } finally {
      setSendingSms(false);
    }
  };

  const passwordLabel =
    authGroup === '53' ? 'کلمه عبور' : authGroup === '54' ? 'کلمه عبور / کد رمزیاب' : 'رمز عبور ایستا';
  const storedUserLabel =
    selectedProfile?.default_username || status?.default_username || username || '—';
  const busy = loadingCaptcha || submitting || sendingSms || switchingProfile;

  return (
    <div className="lg:col-span-3 flex justify-center">
      <Card className="w-full max-w-xl border-slate-200 shadow-sm">
        <CardHeader className="space-y-4 border-b border-slate-100 pb-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
              <Shield className="h-5 w-5" />
            </span>
            <div className="min-w-0 space-y-1">
              <CardTitle className="text-lg">ورود به اینترنت‌بانک</CardTitle>
              <CardDescription>
                {isSmsFlow
                  ? step === 1
                    ? 'مرحله ۱ از ۲ — اطلاعات ورود و ارسال پیامک'
                    : 'مرحله ۲ از ۲ — تأیید کد پیامک'
                  : authGroup === '53'
                    ? 'ورود با رمزیاب سخت‌افزاری (بدون کپچا)'
                    : 'ورود با نرم‌افزار موبایل / USSD (بدون کپچا)'}
              </CardDescription>
            </div>
          </div>

          {isSmsFlow ? (
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'flex h-7 flex-1 items-center justify-center rounded-md text-xs font-medium',
                  step === 1 ? 'bg-slate-900 text-white' : 'bg-emerald-100 text-emerald-800',
                )}
              >
                ۱. ارسال پیامک
              </div>
              <div className="h-px w-4 bg-slate-200" />
              <div
                className={cn(
                  'flex h-7 flex-1 items-center justify-center rounded-md text-xs font-medium',
                  step === 2 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500',
                )}
              >
                ۲. ورود
              </div>
            </div>
          ) : null}
        </CardHeader>

        <CardContent className="pt-5">
          <form className="space-y-5" onSubmit={handleLogin}>
            {credentialProfiles.length > 0 ? (
              <div className="space-y-2">
                <Label htmlFor="bsi-account">حساب / نام کاربری</Label>
                {credentialProfiles.length === 1 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800">
                    <div className="font-medium">{credentialProfiles[0].name}</div>
                    <div className="mt-0.5 text-xs text-slate-500" dir="ltr">
                      {credentialProfiles[0].default_username}
                      {credentialProfiles[0].owner_name ? ` · ${credentialProfiles[0].owner_name}` : ''}
                    </div>
                    <div className="mt-1 text-[11px] text-emerald-700">به‌صورت خودکار انتخاب شد</div>
                  </div>
                ) : (
                  <select
                    id="bsi-account"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={selectedProfile?.id ?? ''}
                    disabled={needsOtp || switchingProfile}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      if (Number.isFinite(id) && id > 0) handleAccountChange(id);
                    }}
                  >
                    <option value="" disabled>
                      انتخاب کنید…
                    </option>
                    {credentialProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {profileOptionLabel(p)}
                      </option>
                    ))}
                  </select>
                )}
                {needsAccountPick ? (
                  <p className="text-xs text-amber-700">چند حساب دارید؛ یکی را برای ورود انتخاب کنید.</p>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-2">
              <Label>روش تأیید</Label>
              <div className="grid grid-cols-3 gap-2">
                {AUTH_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={needsOtp && opt.value !== '52'}
                    onClick={() => handleAuthChange(opt.value)}
                    className={cn(
                      'rounded-lg border px-2 py-2.5 text-center transition-colors',
                      authGroup === opt.value
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50',
                      needsOtp && opt.value !== '52' && 'cursor-not-allowed opacity-40',
                    )}
                  >
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className={cn('mt-0.5 text-[10px]', authGroup === opt.value ? 'text-slate-300' : 'text-slate-500')}>
                      {opt.hint}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {step === 1 ? (
              useStoredCredentials ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
                  نام کاربری و رمز از حساب انتخاب‌شده خوانده می‌شود
                  <div className="mt-1 font-mono text-xs text-emerald-800" dir="ltr">
                    {storedUserLabel}
                    <span className="mx-1.5 opacity-50">·</span>
                    ••••••••
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="bsi-user">نام کاربری</Label>
                    <Input
                      id="bsi-user"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      required={!hasStoredUser}
                      placeholder={hasStoredUser ? selectedProfile?.default_username || undefined : undefined}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bsi-pass">{passwordLabel}</Label>
                    <Input
                      id="bsi-pass"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      placeholder={
                        authGroup === '54'
                          ? 'کد نرم‌افزار موبایل یا USSD'
                          : hasStoredPass
                            ? 'خالی = رمز ذخیره‌شده حساب'
                            : undefined
                      }
                      required={authGroup === '54' || !hasStoredPass}
                    />
                  </div>
                </div>
              )
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                کاربر <span className="font-medium">{storedUserLabel || status?.username_masked || '—'}</span>
                <span className="mx-2 text-slate-300">·</span>
                پیامک ارسال شده — کد را وارد کنید
              </div>
            )}

            {step === 2 ? (
              <div className="space-y-2">
                <Label htmlFor="bsi-otp">شناسه تأیید پیامک</Label>
                <div className="relative">
                  <MessageSquare className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="bsi-otp"
                    className="pr-10"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="کد دریافتی"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </div>
              </div>
            ) : null}

            {showCaptcha ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>کپچا</Label>
                  {ocrAvailable === true ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                      <Sparkles className="h-3 w-3" />
                      {captchaAutoFilled ? 'خودکار پر شد' : 'OCR فعال'}
                    </span>
                  ) : null}
                </div>
                {forceCaptcha && !authNeedsCaptchaByDefault(authGroup) ? (
                  <p className="text-xs text-amber-700">بانک پس از چند تلاش ناموفق، کپچا خواسته است.</p>
                ) : null}
                <div className="flex items-stretch gap-2">
                  <div className="flex h-11 w-[148px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white">
                    {loadingCaptcha || switchingProfile ? (
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    ) : captchaSrc ? (
                      <img src={captchaSrc} alt="کپچا" className="h-full w-full object-contain p-1" />
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-11 w-11 shrink-0"
                    onClick={() => void ensureLoginSession({ force: false, withImage: true })}
                    disabled={busy}
                    title="کپچای جدید"
                  >
                    <RefreshCw className={cn('h-4 w-4', loadingCaptcha && 'animate-spin')} />
                  </Button>
                  <Input
                    className="h-11 flex-1 font-mono tracking-wider"
                    value={captcha}
                    onChange={(e) => {
                      setCaptcha(e.target.value);
                      setCaptchaAutoFilled(false);
                    }}
                    placeholder="کد تصویر"
                    required
                    autoComplete="off"
                    dir="ltr"
                  />
                </div>
              </div>
            ) : null}

            <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <Button type="button" variant="ghost" className="text-slate-600" onClick={resetFlow} disabled={busy}>
                شروع مجدد
              </Button>

              <div className="flex flex-col gap-2 sm:flex-row">
                {isSmsFlow && step === 1 ? (
                  <Button
                    type="button"
                    className="min-w-[140px]"
                    disabled={busy || needsAccountPick}
                    onClick={() => void handleSendSms()}
                  >
                    {sendingSms || switchingProfile ? (
                      <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                    ) : (
                      <MessageSquare className="ml-1 h-4 w-4" />
                    )}
                    ارسال پیامک
                  </Button>
                ) : (
                  <Button type="submit" className="min-w-[140px]" disabled={busy || needsAccountPick}>
                    {submitting || switchingProfile ? (
                      <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                    ) : (
                      <KeyRound className="ml-1 h-4 w-4" />
                    )}
                    ورود
                  </Button>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BsiLoginTab;
